import { useContext, useMemo } from "react";
import { BacktestDetailsContext } from "@shared/features/bots/BacktestDetails.context";
import PieChart from "common/Charts/PieChart/PieChart";
import Title from "common/Typography/Title";
import {
  BacktestingStatisticCard,
  NewLookStatisticTitle,
} from "../../common/styles";
import AllocationDonut from "common/NewLook/AllocationDonut";
import { useTheme } from "styled-components";
import EmptyView from "common/Empty/EmptyView";
import Text from "common/Typography/Text";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import { typography } from "@shared/common/Themes/typography";
import { useMostTradedCoinsStatistic } from "@shared/features/bots/BotForm/Backtesting/hooks";
import { EmptyStatesType } from "common/Icons/types.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

const MostTradedCoinsStatistic = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { pieChartData, isEmpty } = useMostTradedCoinsStatistic();
  const theme = useTheme();
  const { liveBacktestingData } = useContext(BacktestDetailsContext) || {};

  const pieChartLegendData = useMemo(
    () =>
      pieChartData.map((dataPoint) => ({
        ...dataPoint,
        legendDisplay: (
          <div>
            <Text
              $size={isMobile ? typography.fontSizes.S : typography.fontSizes.M}
            >
              {dataPoint.name}
            </Text>
            <Text type="secondary" $size={typography.fontSizes.S}>
              {dataPoint.value}%
            </Text>
          </div>
        ),
      })),
    [pieChartData]
  );

  if (theme.isNewLook) {
    const total = Object.values(
      liveBacktestingData?.asset_to_counter || {}
    ).reduce((sum, value) => sum + value, 0);
    return (
      <BacktestingStatisticCard
        style={{ gridArea: "MostTradedCoins" }}
        $isMobile={isMobile}
      >
        <NewLookStatisticTitle>
          {t("statistics.most_traded_coins")}
        </NewLookStatisticTitle>
        {!isEmpty ? (
          <AllocationDonut
            bare={true}
            compact={true}
            chartId="mostTradedCoinsDonut"
            data={pieChartData}
            total={total}
          />
        ) : (
          <EmptyView
            iconKey={EmptyStatesType.Trades}
            title={t("empty_states.no_coins")}
            iconWidth={64}
          />
        )}
      </BacktestingStatisticCard>
    );
  }

  return (
    <BacktestingStatisticCard
      style={{ gridArea: "MostTradedCoins" }}
      $isMobile={isMobile}
    >
      <Title type="secondary" level={3}>
        {t("statistics.most_traded_coins")}
      </Title>
      {!isEmpty ? (
        <PieChart
          data={pieChartLegendData}
          horizontal={true}
          extraInfoOnTheLeft={true}
          hideTotal={true}
          numItemsInRow={2}
          numRows={3}
          chartSize={isMobile ? 120 : 160}
          chartWeight={isMobile ? "84%" : "88%"}
          chartParentElementId="mostTradedCoinsPieChart"
          tooltipElementId="mostTradedCoinsTooltip"
          extraInfoStyle={EXTRA_INFO_STYLE}
          chartStyle={CHART_STYLE}
          gap={CHART_GAP}
          hideTooltip={isMobile}
          disableHover={isMobile}
        />
      ) : (
        <EmptyView
          iconKey={EmptyStatesType.Trades}
          title={t("empty_states.no_coins")}
          iconWidth={80}
        />
      )}
    </BacktestingStatisticCard>
  );
};

export default MostTradedCoinsStatistic;

const EXTRA_INFO_STYLE = { margin: 0 };
const CHART_STYLE = { margin: 0, flex: "none" };
const CHART_GAP: [number, number] = [8, 0];
